// TUT-541 — o pedido de proposta pelo site: os formulários de cada site
// (Configurações › Atendimento), os pedidos que chegaram (o quadro de
// Propostas) e a porta pública, sem login (`/pedir-proposta/<chave>`).
import {
  demoArquivarPedidoDeProposta,
  demoCriarFormularioDePedido,
  demoDesativarFormularioDePedido,
  demoEnviarPedidoDeProposta,
  demoFormularioPublicoDePedido,
  demoFormulariosDePedido,
  demoPedidosDeProposta,
} from "../demo/pedidos-de-proposta";
import { enviarAoArmazenamento } from "../upload-transport";
import type {
  EnvioDePedidoDeProposta,
  FormularioDePedidoDeProposta,
  FormulariosDePedidoResponse,
  FormularioPublicoDePedido,
  PedidoDeProposta,
  PedidosDePropostaResponse,
  ResultadoDoPedidoDeProposta,
} from "../types";
import { request, sha256 } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

function caminhoPublico(chave: string): string {
  return `/public/pedidos-de-proposta/${encodeURIComponent(chave)}`;
}

export const apiDosPedidosDeProposta = {
  formulariosDePedido(): Promise<FormulariosDePedidoResponse> {
    if (useDemoApi) return demoFormulariosDePedido();
    return request<FormulariosDePedidoResponse>("/pedidos-de-proposta/formularios");
  },
  criarFormularioDePedido(input: { name: string; websiteUrl: string }): Promise<FormularioDePedidoDeProposta> {
    if (useDemoApi) return demoCriarFormularioDePedido(input);
    return request<FormularioDePedidoDeProposta>("/pedidos-de-proposta/formularios", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  desativarFormularioDePedido(id: string): Promise<FormularioDePedidoDeProposta> {
    if (useDemoApi) return demoDesativarFormularioDePedido(id);
    return request<FormularioDePedidoDeProposta>(
      `/pedidos-de-proposta/formularios/${encodeURIComponent(id)}/desativar`,
      { method: "POST" },
    );
  },
  pedidosDeProposta(): Promise<PedidosDePropostaResponse> {
    if (useDemoApi) return demoPedidosDeProposta();
    return request<PedidosDePropostaResponse>("/pedidos-de-proposta");
  },
  arquivarPedidoDeProposta(id: string): Promise<PedidoDeProposta> {
    if (useDemoApi) return demoArquivarPedidoDeProposta(id);
    return request<PedidoDeProposta>(`/pedidos-de-proposta/${encodeURIComponent(id)}/arquivar`, { method: "POST" });
  },
  async anexoDoPedidoDeProposta(id: string): Promise<string> {
    if (useDemoApi) return "about:blank";
    return (await request<{ url: string }>(`/pedidos-de-proposta/${encodeURIComponent(id)}/anexo`)).url;
  },

  /** A porta pública: a quem o pedido vai, o texto do consentimento e o limite do anexo. */
  formularioPublicoDePedido(chave: string): Promise<FormularioPublicoDePedido> {
    if (useDemoApi) return demoFormularioPublicoDePedido(chave);
    return request<FormularioPublicoDePedido>(caminhoPublico(chave));
  },
  /**
   * Manda o pedido e, se houver arquivo, o anexo: o servidor grava o pedido e
   * devolve o link de envio; o arquivo vai direto ao armazenamento; o servidor
   * confere (tamanho, tipo, hash e antivírus). O pedido vale mesmo se o anexo
   * falhar — a tela diz qual dos dois aconteceu.
   */
  async enviarPedidoDeProposta(chave: string, envio: EnvioDePedidoDeProposta): Promise<ResultadoDoPedidoDeProposta> {
    if (useDemoApi) return demoEnviarPedidoDeProposta(chave, envio);
    const arquivo = envio.arquivo;
    const resposta = await request<{
      recebido: true;
      anexo: { envio: string; upload: { url: string; method: "PUT"; headers: Record<string, string> } } | null;
    }>(caminhoPublico(chave), {
      method: "POST",
      body: JSON.stringify({
        empresa: envio.empresa,
        nome: envio.nome,
        email: envio.email,
        telefone: envio.telefone,
        objeto: envio.objeto,
        consentimento: envio.consentimento,
        site: envio.site,
        ...(arquivo
          ? {
              anexo: {
                originalName: arquivo.name,
                mimeType: arquivo.type,
                sizeBytes: arquivo.size,
                checksumSha256: await sha256(arquivo),
              },
            }
          : {}),
      }),
    });
    if (!arquivo) return { recebido: true, anexo: "sem-anexo" };
    if (!resposta.anexo) return { recebido: true, anexo: "falhou" };
    try {
      const enviado = await enviarAoArmazenamento({
        url: resposta.anexo.upload.url,
        method: resposta.anexo.upload.method,
        headers: resposta.anexo.upload.headers,
        corpo: arquivo,
      });
      if (!enviado.ok) return { recebido: true, anexo: "falhou" };
      await request(`/public/pedidos-de-proposta/anexos/${encodeURIComponent(resposta.anexo.envio)}/concluir`, {
        method: "POST",
      });
      return { recebido: true, anexo: "enviado" };
    } catch {
      return { recebido: true, anexo: "falhou" };
    }
  },
};
